// helper function to change the color to darker or lighter (colorCode,50 or -50)
/* eslint-disable no-bitwise */
const newShade = (color, magnitude) => {
  const hexColor = color.replace(`#`, ``);
  if (hexColor.length === 6) {
    const decimalColor = parseInt(hexColor, 16);
    let r = (decimalColor >> 16) + magnitude;
    r = Math.min(255, Math.max(0, r));
    let g = ((decimalColor >> 8) & 0x00ff) + magnitude;
    g = Math.min(255, Math.max(0, g));
    let b = (decimalColor & 0x0000ff) + magnitude;
    b = Math.min(255, Math.max(0, b));
    const newColor = (r << 16) | (g << 8) | b;
    return `#${newColor.toString(16).padStart(6, '0')}`;
  }
  return hexColor;
};

// Convert #RRGGBB → rgba(r, g, b, alpha) string. Used to derive translucent
// brand tints/focus rings without relying on SCSS rgba() (which can't accept
// a CSS var as an argument).
const hexToRgba = (color, alpha) => {
  const hexColor = String(color).replace('#', '');
  if (hexColor.length !== 6) return color;
  const r = parseInt(hexColor.slice(0, 2), 16);
  const g = parseInt(hexColor.slice(2, 4), 16);
  const b = parseInt(hexColor.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Mix a hex color with white at a given percent (0–100). Used to derive
// Material 3 surface tones from the tenant primary. Returning a hex (not a
// CSS color-mix() string) so consumers like AuthWrapper that apply it via
// inline style still work everywhere.
const mixHexWithWhite = (color, percent) => {
  const hexColor = String(color).replace('#', '');
  if (hexColor.length !== 6) return color;
  const r = parseInt(hexColor.slice(0, 2), 16);
  const g = parseInt(hexColor.slice(2, 4), 16);
  const b = parseInt(hexColor.slice(4, 6), 16);
  const p = Math.max(0, Math.min(100, percent)) / 100;
  const mix = channel => Math.round(channel * p + 255 * (1 - p));
  const toHex = n => n.toString(16).padStart(2, '0');
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
};

// Material 3 surface tone percentages (light theme). Numbers represent how
// much of the tenant primary is mixed into white — small values yield
// near-white surfaces with a perceptible brand hue, mirroring the M3 tonal
// palette (surface ≈ tone 98, container ≈ tone 94, high ≈ tone 92).
const SURFACE_TONE = 4;
const SURFACE_CONTAINER_TONE = 8;
const SURFACE_CONTAINER_HIGH_TONE = 14;
// Material on-surface for light theme (tone 10). Near-black with a hint of
// primary hue would be more authentic to M3, but a fixed slate keeps copy
// legible across all tenants without per-tenant tuning.
const ON_SURFACE = '#1c1b1f';

// Per-tenant raw configuration. Only the brand-identifying fields live here;
// surface tones and on-surface text are derived below from `primary`.
// primary: '#fe6f61',
const baseTenants = {
  default: {
    deviceId: 2,
    primary: '#009de1',
    logoBg: '#ffffff',
    logo: '/images/new-brand-logo.svg',
    welcomeImg: '/images/pl-default-welcome.png',
    welcomeImgWebp: '/images/pl-default-welcome.webp',
  },
  lopay: {
    deviceId: 5,
    primary: '#727eb9',
    logoBg: '#ffffff',
    logo: '/images/psp/lopay.png',
    welcomeImg: '/images/lopay-welcome.png',
    welcomeImgWebp: '/images/lopay-welcome.webp',
  },
  handepay: {
    deviceId: 4,
    primary: '#0171bb',
    logoBg: '#0171bb',
    logo: '/images/handepay-logo.png',
    welcomeImg: '/images/castles-welcome.png',
    welcomeImgWebp: '/images/castles-welcome.webp',
  },
  // Add more clients as needed
};

// Build a full tenant config from the raw entry, deriving Material surface
// tones and the standard button/link/on-primary fields from `primary`.
function buildTenantConfig(raw) {
  const { primary } = raw;
  return {
    ...raw,
    background: mixHexWithWhite(primary, SURFACE_TONE),
    onBackground: ON_SURFACE,
    primaryButtonColor: primary,
    onPrimaryButton: '#ffffff',
    linkColor: primary,
  };
}

export const themeProperties = Object.fromEntries(
  Object.entries(baseTenants).map(([name, raw]) => [name, buildTenantConfig(raw)]),
);

// Short brand codes → canonical tenant keys, so a theme can be applied by the
// short name (pl / lp / hp) as well as the full brand name
// (payment-loyalty / lopay / handepay). Full names that aren't a direct key
// (payment-loyalty → default) are covered here too.
const TENANT_ALIASES = {
  pl: 'default',
  paymentloyalty: 'default',
  lp: 'lopay',
  hp: 'handepay',
};

// Normalise a raw client name (strip dashes, lowercase), resolve any short
// alias, and return a valid tenant key — falling back to 'default' for
// anything unknown.
export function resolveTenantKey(clientName) {
  const normalized = String(clientName || '')
    .replace(/-/g, '')
    .toLowerCase();
  const key = TENANT_ALIASES[normalized] || normalized;
  return themeProperties[key] ? key : 'default';
}

// Define the function to get client style based on client name
export default function getClientConfigByName(clientName) {
  return themeProperties[resolveTenantKey(clientName)];
}

// Build the :root CSS custom property map for a given tenant config. Consumed
// by _document.js (SSR injection) and ThemeProvider (runtime sync). Keep the
// keys in sync with the $brand-* tokens in src/styles/variables.scss.
export function getBrandCssVars(clientConfig) {
  const primary = clientConfig?.primary || themeProperties.default.primary;
  const hover =
    clientConfig?.primaryButtonColor && clientConfig.primaryButtonColor !== primary
      ? clientConfig.primaryButtonColor
      : newShade(primary, -30);
  const link = clientConfig?.linkColor || primary;
  const onPrimary = clientConfig?.onPrimaryButton || '#ffffff';

  return {
    '--brand-primary': primary,
    '--brand-primary-hover': hover,
    '--brand-on-primary': onPrimary,
    '--brand-link': link,
    '--brand-focus-ring': hexToRgba(primary, 0.2),
    '--brand-focus-ring-soft': hexToRgba(primary, 0.08),
    '--brand-tint': hexToRgba(primary, 0.06),
    // Material 3 surface tones — page backdrop and container layers. Derived
    // from primary so each tenant gets a coherent near-white surface with a
    // faint brand hue.
    '--brand-surface': mixHexWithWhite(primary, SURFACE_TONE),
    '--brand-surface-container': mixHexWithWhite(primary, SURFACE_CONTAINER_TONE),
    '--brand-surface-container-high': mixHexWithWhite(primary, SURFACE_CONTAINER_HIGH_TONE),
    '--brand-on-surface': ON_SURFACE,
  };
}

// Render the brand vars as the body of a `:root { ... }` block. SSR uses this
// to inject a `<style>` block during getInitialProps so the first paint is
// already branded.
export function buildBrandCssVarsBlock(clientConfig) {
  const vars = getBrandCssVars(clientConfig);
  const lines = Object.entries(vars)
    .map(([key, value]) => `${key}:${value};`)
    .join('');
  return `:root{${lines}}`;
}

export { newShade, hexToRgba, mixHexWithWhite };
